import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, MessageSquare, Trash2, Download, Pencil, X, Image as ImageIcon, FileBox, Eye, EyeOff, KeyRound } from "lucide-react";
import STLViewer from "@/components/STLViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STAGES = [
    "order_received", "photos_verified",
    "production", "printing_3d", "painting", "quality_check", "packing", "shipping", "delivered"
];

const extractExt = (url) => {
    const clean = url.split("?")[0];
    const m = clean.match(/\.[a-zA-Z0-9]{2,5}$/);
    return m ? m[0] : ".jpg";
};

// Fetch an admin-only miniature file with the auth token, then open/download it.
const openAdminFile = async (endpoint, filename, download = false) => {
    try {
        const res = await api.get(endpoint, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        if (download) {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
        toast.error("File not available for this item");
    }
};

const downloadPhoto = async (src, filename) => {
    try {
        const resp = await fetch(src, { credentials: "omit" });
        if (!resp.ok) throw new Error("bad response");
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
        // Fallback: open in new tab
        window.open(src, "_blank");
    }
};

const downloadAllPhotos = async (paths, prefix) => {
    for (let i = 0; i < paths.length; i++) {
        const p = paths[i];
        const src = p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`;
        await downloadPhoto(src, `${prefix}-photo-${i + 1}${extractExt(src)}`);
        await new Promise((r) => setTimeout(r, 250));
    }
};

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState("");
    const [creating, setCreating] = useState(false);
    const [manual, setManual] = useState(null);
    const [checkedIds, setCheckedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const allChecked = orders.length > 0 && orders.every((o) => checkedIds.has(o.id));
    const someChecked = checkedIds.size > 0 && !allChecked;
    const toggleOne = (id) => {
        setCheckedIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };
    const toggleAll = () => {
        setCheckedIds(allChecked ? new Set() : new Set(orders.map((o) => o.id)));
    };
    const clearSelection = () => setCheckedIds(new Set());

    const bulkDelete = async () => {
        if (checkedIds.size === 0) return;
        if (!window.confirm(`Delete ${checkedIds.size} selected orders? This cannot be undone.`)) return;
        setBulkBusy(true);
        const ids = Array.from(checkedIds);
        let ok = 0;
        for (const id of ids) {
            try { await api.delete(`/orders/admin/${id}`); ok++; } catch {}
        }
        setOrders((os) => os.filter((o) => !checkedIds.has(o.id)));
        clearSelection();
        setBulkBusy(false);
        toast.success(`Deleted ${ok} of ${ids.length} orders`);
    };

    const bulkChangeStage = async (stage) => {
        if (checkedIds.size === 0 || !stage) return;
        setBulkBusy(true);
        const ids = Array.from(checkedIds);
        let ok = 0;
        for (const id of ids) {
            try { await api.patch(`/orders/admin/${id}/status`, { stage }); ok++; } catch {}
        }
        setBulkBusy(false);
        toast.success(`Updated ${ok} of ${ids.length} orders → ${stage.replace(/_/g, " ")}`);
        load();
        clearSelection();
    };

    const load = async () => {
        setLoading(true);
        try {
            const [o, p] = await Promise.all([api.get("/orders/admin/all"), api.get("/products", { params: { limit: 200 } })]);
            setOrders(o.data.orders || []);
            setProducts(p.data.products || []);
        } catch (e) {
            toast.error("Failed to load orders");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updateStage = async (order, stage) => {
        try { await api.patch(`/orders/admin/${order.id}/status`, { stage }); toast.success("Status updated"); load(); }
        catch { toast.error("Failed"); }
    };

    const deleteOrder = async (order, e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
        try {
            await api.delete(`/orders/admin/${order.id}`);
            toast.success("Order deleted");
            setOrders((os) => os.filter((o) => o.id !== order.id));
            if (selected?.id === order.id) setSelected(null);
        } catch (e2) { toast.error(e2.response?.data?.detail || "Failed to delete"); }
    };

    const [loginInfo, setLoginInfo] = useState(null); // { has_login, ... } for selected order
    const [loginResult, setLoginResult] = useState(null); // response of create-login (temp password shown once)
    const [loginBusy, setLoginBusy] = useState(false);

    const openDetail = async (o) => {
        setSelected(o);
        setLoginInfo(null);
        setLoginResult(null);
        try { const { data } = await api.get(`/orders/admin/${o.id}/notes`); setNotes(data.notes || []); } catch {}
        try { const { data } = await api.get(`/orders/admin/${o.id}/customer-login`); setLoginInfo(data); } catch {}
    };

    const createCustomerLogin = async (reset = false) => {
        if (!selected) return;
        if (reset && !window.confirm("Generate a new temporary password for this customer? Their current password will stop working.")) return;
        setLoginBusy(true);
        try {
            const { data } = await api.post(`/orders/admin/${selected.id}/create-login`, { reset_password: reset });
            setLoginResult(data);
            setLoginInfo((s) => ({ ...(s || {}), has_login: true, orders_linked: true, email: data.email }));
            toast.success(data.created ? `Login created · ${data.linked_orders} order(s) linked` : `Account updated · ${data.linked_orders} order(s) linked`);
            if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to create login"); }
        finally { setLoginBusy(false); }
    };

    const removeDetailProductionFile = async (idx, path) => {
        if (!selected) return;
        const fileName = (path || "").split("/").pop();
        if (!window.confirm(`Delete "${fileName}"? The customer will no longer see it in their Studio.`)) return;
        try {
            await api.delete(`/orders/admin/${selected.id}/items/${idx}/production-file`, { params: { path } });
            const items = selected.items.map((it, i) => i === idx ? { ...it, production_files: (it.production_files || []).filter((p) => p !== path) } : it);
            setSelected({ ...selected, items });
            setOrders((os) => os.map((o) => o.id === selected.id ? { ...o, items } : o));
            toast.success("File deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const addNote = async () => {
        if (!noteText.trim()) return;
        try {
            const { data } = await api.post(`/orders/admin/${selected.id}/notes`, { body: noteText });
            setNotes((n) => [...n, data]);
            setNoteText("");
        } catch { toast.error("Failed"); }
    };

    const startManual = () => setManual({
        _id: null,
        _editing: false,
        items: [{ product_id: products[0]?.id || "", size_label: products[0]?.sizes?.[0]?.label || "", quantity: 1, reference_photos: [] }],
        address: { full_name: "", phone: "", email: "", line1: "", city: "", state: "", pincode: "", country: "India" },
        notes: "",
        mark_paid: false,
    });

    const startEdit = (o) => setManual({
        _id: o.id,
        _editing: true,
        _orderNumber: o.order_number,
        items: (o.items || []).map((it) => ({
            product_id: it.product_id,
            product_name: it.product_name,
            size_label: it.size_label,
            quantity: it.quantity || 1,
            reference_photos: it.reference_photos || [],
            production_files: it.production_files || [],
            selected_options: it.selected_options || {},
            notes: it.notes || "",
        })),
        address: {
            full_name: o.address?.full_name || "",
            phone: o.address?.phone || "",
            email: o.address?.email || "",
            line1: o.address?.line1 || "",
            city: o.address?.city || "",
            state: o.address?.state || "",
            pincode: o.address?.pincode || "",
            country: o.address?.country || "India",
        },
        notes: (o.notes || "").replace(" · Manual order by admin", ""),
        mark_paid: o.payment_status === "paid",
    });

    const uploadManualPhoto = async (idx, file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        form.append("kind", "reference_photo");
        try {
            const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            const items = [...manual.items];
            items[idx] = { ...items[idx], reference_photos: [...(items[idx].reference_photos || []), data.path] };
            setManual({ ...manual, items });
            toast.success("Photo uploaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        }
    };

    const [prodUpload, setProdUpload] = useState(null); // { idx, name, progress }
    const [previewFile, setPreviewFile] = useState(null); // { key: "idx-fi", path } of a published file being previewed inline
    const uploadHref = (p) => (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`);
    const isMeshFile = (p) => /\.(stl|3mf|glb|gltf)$/i.test(p || "");

    const uploadProductionFile = async (idx, file) => {
        if (!file || !manual?._id) return;
        const form = new FormData();
        form.append("file", file);
        form.append("kind", "production_file");
        setProdUpload({ idx, name: file.name, progress: 0 });
        try {
            const { data } = await api.post("/uploads", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    const pct = Math.round((evt.loaded * 100) / evt.total);
                    setProdUpload((s) => (s && s.idx === idx ? { ...s, progress: pct } : s));
                },
            });
            setProdUpload((s) => (s && s.idx === idx ? { ...s, progress: 100 } : s));
            setManual((m) => {
                const items = [...m.items];
                items[idx] = { ...items[idx], staged_production_files: [...(items[idx].staged_production_files || []), data.path] };
                return { ...m, items };
            });
            toast.success("File uploaded — check the preview, then click Save changes to publish it to the customer's Studio");
            if (data.draco?.compressed && !data.draco.already) {
                const kb = (n) => `${Math.round(n / 1024)} KB`;
                toast.message(`GLB compressed with Draco: ${kb(data.draco.original_bytes)} → ${kb(data.draco.bytes)}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            // Clear the progress row after a short beat so the user sees "100%"
            setTimeout(() => setProdUpload((s) => (s && s.idx === idx ? null : s)), 700);
        }
    };

    const removeStagedFile = (idx, path) => {
        setManual((m) => {
            const items = [...m.items];
            items[idx] = { ...items[idx], staged_production_files: (items[idx].staged_production_files || []).filter((p) => p !== path) };
            return { ...m, items };
        });
    };

    const publishStagedFiles = async (orderId, items) => {
        let notif = null;
        let published = 0;
        for (let idx = 0; idx < items.length; idx++) {
            for (const path of items[idx].staged_production_files || []) {
                const { data } = await api.post(`/orders/admin/${orderId}/items/${idx}/production-file`, { path });
                removeStagedFile(idx, path);
                notif = data;
                published++;
            }
        }
        if (!published) return;
        toast.success(`${published} production file${published > 1 ? "s" : ""} published — now visible on the customer's Studio`);
        if (notif?.whatsapp_url) {
            window.open(notif.whatsapp_url, "_blank", "noopener,noreferrer");
            toast.success("Opening WhatsApp to notify the customer…");
        } else if (notif && !notif.customer_phone) {
            toast.message("No customer phone on file — email notification sent instead.");
        }
    };

    const removeProductionFile = async (idx, path) => {
        if (!manual?._id) return;
        const fileName = (path || "").split("/").pop();
        if (!window.confirm(`Remove production file "${fileName}"? The customer will no longer see it in their Studio.`)) return;
        try {
            await api.delete(`/orders/admin/${manual._id}/items/${idx}/production-file`, { params: { path } });
            const items = [...manual.items];
            items[idx] = {
                ...items[idx],
                production_files: (items[idx].production_files || []).filter((p) => p !== path),
            };
            setManual({ ...manual, items });
            toast.success("Production file removed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Remove failed");
        }
    };

    const removeManualPhoto = (idx, photoIdx) => {
        const items = [...manual.items];
        items[idx] = { ...items[idx], reference_photos: (items[idx].reference_photos || []).filter((_, i) => i !== photoIdx) };
        setManual({ ...manual, items });
    };

    const createManual = async () => {
        if (!manual.address.full_name || !manual.address.phone || !manual.address.email) { toast.error("Name, phone, email required"); return; }
        setCreating(true);
        try {
            if (manual._editing && manual._id) {
                await api.patch(`/orders/admin/${manual._id}/edit`, {
                    items: manual.items.map(({ staged_production_files, ...it }) => it),
                    address: manual.address,
                    notes: manual.notes,
                    mark_paid: manual.mark_paid,
                });
                await publishStagedFiles(manual._id, manual.items);
                toast.success("Order updated");
            } else {
                await api.post("/orders/admin/manual", manual);
                toast.success("Manual order created 🎉");
            }
            setManual(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setCreating(false); }
    };

    return (
        <div className="p-8" data-testid="admin-orders">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Orders</h1>
                    <p className="text-sm text-muted-foreground">Manage the production pipeline. Total: {orders.length}</p>
                </div>
                <Button onClick={startManual} data-testid="manual-order-btn" className="rounded-full bg-primary hover:bg-primary/90"><Plus className="w-4 h-4" /> Manual order</Button>
            </div>

            {checkedIds.size > 0 && (
                <div className="mb-4 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="orders-bulk-bar">
                    <div className="text-sm font-semibold" data-testid="orders-bulk-count">{checkedIds.size} selected</div>
                    <select
                        onChange={(e) => { if (e.target.value) { bulkChangeStage(e.target.value); e.target.value = ""; } }}
                        disabled={bulkBusy}
                        data-testid="orders-bulk-stage"
                        className="rounded-lg border px-2 h-9 text-sm bg-white"
                        defaultValue=""
                    >
                        <option value="" disabled>Change stage to…</option>
                        {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                    <Button
                        onClick={bulkDelete}
                        disabled={bulkBusy}
                        variant="destructive"
                        data-testid="orders-bulk-delete"
                        className="rounded-full h-9"
                    >
                        <Trash2 className="w-4 h-4" /> Delete selected
                    </Button>
                    <button onClick={clearSelection} disabled={bulkBusy} className="text-xs text-muted-foreground underline ml-auto" data-testid="orders-bulk-clear">Clear</button>
                </div>
            )}

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
                <div className="rounded-2xl bg-card border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left">
                            <tr>
                                <th className="p-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                        onChange={toggleAll}
                                        data-testid="orders-select-all"
                                        aria-label="Select all orders"
                                    />
                                </th>
                                <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Stage</th><th className="p-3">When</th><th className="p-3">Photos</th><th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const isChecked = checkedIds.has(o.id);
                                return (
                                    <tr key={o.id} className={`border-t border-border hover:bg-muted/20 cursor-pointer ${isChecked ? "bg-primary/5" : ""}`} onClick={() => openDetail(o)} data-testid={`order-row-${o.order_number}`}>
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleOne(o.id)}
                                                data-testid={`order-select-${o.order_number}`}
                                                aria-label={`Select order ${o.order_number}`}
                                            />
                                        </td>                                        <td className="p-3 font-mono text-xs">{o.order_number}</td>
                                        <td className="p-3">{o.address?.full_name}<div className="text-xs text-muted-foreground">{o.address?.phone}</div></td>
                                        <td className="p-3">{formatINR(o.total_inr)}</td>
                                        <td className="p-3">
                                            <span className={`text-[11px] px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"}`}>{o.payment_status}</span>
                                        </td>
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            <select value={o.status} onChange={(e) => updateStage(o, e.target.value)} className="rounded-lg border px-2 h-8 text-xs">
                                                {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                                            </select>
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</td>
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            {(() => {
                                                const allPhotos = (o.items || []).flatMap((it) => it.reference_photos || []);
                                                if (allPhotos.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
                                                const firstSrc = allPhotos[0].startsWith("http") ? allPhotos[0] : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${allPhotos[0]}`;
                                                return (
                                                    <div className="flex items-center gap-2" data-testid={`order-photos-cell-${o.order_number}`}>
                                                        <button
                                                            onClick={() => openDetail(o)}
                                                            title="View photos"
                                                            className="relative w-10 h-10 rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary transition shrink-0"
                                                        >
                                                            <img src={firstSrc} alt="" className="w-full h-full object-cover" />
                                                            {allPhotos.length > 1 && (
                                                                <span className="absolute bottom-0 right-0 text-[9px] font-bold bg-primary text-white rounded-tl-md px-1">+{allPhotos.length - 1}</span>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => downloadAllPhotos(allPhotos, o.order_number)}
                                                            title={`Download all ${allPhotos.length} photos`}
                                                            data-testid={`order-photos-download-${o.order_number}`}
                                                            className="w-8 h-8 rounded-lg bg-primary/10 hover:bg-primary hover:text-white text-primary flex items-center justify-center transition"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); startEdit(o); }}
                                                    data-testid={`order-edit-${o.order_number}`}
                                                    title="Edit order"
                                                    className="w-8 h-8 rounded-lg hover:bg-primary/10 text-primary"
                                                >
                                                    <Pencil className="w-4 h-4 mx-auto" />
                                                </button>
                                                <button
                                                    onClick={(e) => deleteOrder(o, e)}
                                                    data-testid={`order-delete-${o.order_number}`}
                                                    title="Delete order"
                                                    className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4 mx-auto" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {orders.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No orders yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Detail dialog */}
            <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
                <DialogContent className="max-w-4xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center justify-between gap-3 pr-8">
                                    <DialogTitle className="font-display text-2xl">Order {selected.order_number}</DialogTitle>
                                    <Button
                                        onClick={() => { const o = selected; setSelected(null); startEdit(o); }}
                                        variant="outline"
                                        data-testid="order-detail-edit"
                                        className="rounded-full h-9"
                                    >
                                        <Pencil className="w-4 h-4" /> Edit
                                    </Button>
                                </div>
                            </DialogHeader>
                            <div className="grid md:grid-cols-2 gap-5 text-sm">
                                <div className="space-y-2">
                                    <h4 className="font-semibold uppercase text-xs tracking-widest text-muted-foreground">Customer</h4>
                                    <p>{selected.address?.full_name}<br />{selected.address?.phone} · {selected.address?.email}</p>
                                    <p className="text-muted-foreground">{selected.address?.line1}, {selected.address?.city} {selected.address?.pincode}, {selected.address?.state}</p>
                                    <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="customer-login-block">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Customer login</div>
                                            {loginInfo ? (
                                                <span className={`text-[11px] px-2 py-0.5 rounded-full ${loginInfo.has_login ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="customer-login-status">
                                                    {loginInfo.has_login ? (loginInfo.orders_linked ? "Has login · orders linked" : "Has login · orders not linked") : "No login yet (guest checkout)"}
                                                </span>
                                            ) : <span className="text-[11px] text-muted-foreground">Checking…</span>}
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            {loginInfo && !loginInfo.has_login && (
                                                <Button size="sm" onClick={() => createCustomerLogin(false)} disabled={loginBusy} data-testid="customer-login-create" className="rounded-full h-8 text-xs bg-primary hover:bg-primary/90">
                                                    {loginBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />} Create login & send details
                                                </Button>
                                            )}
                                            {loginInfo && loginInfo.has_login && (
                                                <>
                                                    {!loginInfo.orders_linked && (
                                                        <Button size="sm" variant="outline" onClick={() => createCustomerLogin(false)} disabled={loginBusy} data-testid="customer-login-link" className="rounded-full h-8 text-xs">Link orders to account</Button>
                                                    )}
                                                    <Button size="sm" variant="outline" onClick={() => createCustomerLogin(true)} disabled={loginBusy} data-testid="customer-login-reset" className="rounded-full h-8 text-xs">
                                                        {loginBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} New password & resend
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                        {loginResult?.temp_password && (
                                            <div className="rounded-xl bg-white border p-2.5 text-xs space-y-1" data-testid="customer-login-result">
                                                <div className="text-[10px] text-amber-700 font-semibold">Shown once — copy it now</div>
                                                <div>Email: <b data-testid="customer-login-email">{loginResult.email}</b></div>
                                                <div className="flex items-center gap-2">Password: <code className="px-1.5 py-0.5 rounded bg-muted font-mono" data-testid="customer-login-password">{loginResult.temp_password}</code>
                                                    <button type="button" onClick={() => { navigator.clipboard?.writeText(`Login: ${loginResult.login_url}\nEmail: ${loginResult.email}\nPassword: ${loginResult.temp_password}`); toast.success("Copied"); }} data-testid="customer-login-copy" className="text-primary underline">Copy</button>
                                                </div>
                                                <div className="text-muted-foreground">{loginResult.emailed ? "Emailed to customer." : "Email not sent (SMTP disabled) — share via WhatsApp or copy."}{loginResult.whatsapp_url && <> <a href={loginResult.whatsapp_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid="customer-login-whatsapp">Send on WhatsApp</a></>}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-xs uppercase mt-4 text-muted-foreground">Payment</div>
                                    <div>Status: <strong>{selected.payment_status}</strong></div>
                                    {selected.razorpay_payment_id && <div className="text-xs font-mono break-all">rzp: {selected.razorpay_payment_id}</div>}
                                    <div className="text-xs">Subtotal: {formatINR(selected.subtotal_inr)} · GST: {formatINR(selected.gst_inr)} · Shipping: {formatINR(selected.shipping_inr)} · Total: <strong>{formatINR(selected.total_inr)}</strong></div>
                                </div>
                                <div>
                                    <h4 className="font-semibold uppercase text-xs tracking-widest text-muted-foreground mb-2">Items</h4>
                                    <div className="space-y-3">
                                        {selected.items.map((it, idx) => (
                                            <div key={idx} className="rounded-2xl bg-muted/40 p-3">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="font-semibold text-sm">{it.product_name}</div>
                                                        <div className="text-xs text-muted-foreground">{it.size_label} · Qty {it.quantity} · {formatINR(it.unit_price_inr)}</div>
                                                    </div>
                                                </div>
                                                {it.selected_options && Object.keys(it.selected_options).length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1">
                                                        {Object.entries(it.selected_options).map(([k, v]) => (
                                                            <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{k}: {v}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {it.notes && <div className="mt-2 text-xs text-muted-foreground">Notes: {it.notes}</div>}
                                                {(it.production_files || []).length > 0 && (
                                                    <div className="mt-3" data-testid={`detail-prod-files-${idx}`}>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Production files (STL/GLB/3MF) · {it.production_files.length}</div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {it.production_files.map((p, fi) => (
                                                                <div key={fi} className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] pr-1 overflow-hidden">
                                                                    <a href={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`} target="_blank" rel="noreferrer" title={p} className="px-2.5 py-1 hover:bg-primary/20 inline-flex items-center gap-1" data-testid={`detail-prod-file-${idx}-${fi}`}>
                                                                        <FileBox className="w-3 h-3" /> {p.split("/").pop()}
                                                                    </a>
                                                                    <button type="button" onClick={() => removeDetailProductionFile(idx, p)} data-testid={`detail-prod-file-delete-${idx}-${fi}`} aria-label="Delete file" title="Delete file" className="ml-1 w-5 h-5 rounded-full bg-white/80 hover:bg-destructive hover:text-white text-destructive inline-flex items-center justify-center">
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {it.miniature_job_id && (
                                                    <div className="mt-3" data-testid={`mini-files-${idx}`}>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Miniature files</div>
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => openAdminFile(`/miniature/admin/job/${it.miniature_job_id}/photo`, `${selected.order_number}-customer-photo.jpg`)}
                                                                className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-1"
                                                                data-testid={`mini-file-photo-${idx}`}
                                                            >
                                                                <ImageIcon className="w-3 h-3" /> Customer photo
                                                            </button>
                                                            <button
                                                                onClick={() => openAdminFile(`/miniature/admin/job/${it.miniature_job_id}/preview`, `${selected.order_number}-2d-design.png`)}
                                                                className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-1"
                                                                data-testid={`mini-file-preview-${idx}`}
                                                            >
                                                                <ImageIcon className="w-3 h-3" /> 2D design image
                                                            </button>
                                                            <button
                                                                onClick={() => openAdminFile(`/miniature/admin/job/${it.miniature_job_id}/stl`, `${selected.order_number}-model.stl`, true)}
                                                                className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-1"
                                                                data-testid={`mini-file-stl-${idx}`}
                                                            >
                                                                <FileBox className="w-3 h-3" /> Download STL
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {(it.reference_photos || []).length > 0 && (
                                                    <div className="mt-3">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Customer photos ({it.reference_photos.length})</div>
                                                            <button
                                                                onClick={() => downloadAllPhotos(it.reference_photos, `${selected.order_number}-item-${idx + 1}`)}
                                                                data-testid={`order-download-all-${idx}`}
                                                                className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-1"
                                                            >
                                                                <Download className="w-3 h-3" /> Download all
                                                            </button>
                                                        </div>
                                                        <div className="flex gap-2 flex-wrap">
                                                            {it.reference_photos.map((p, i) => {
                                                                const src = p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`;
                                                                const filename = `${selected.order_number}-item-${idx + 1}-photo-${i + 1}${extractExt(src)}`;
                                                                return (
                                                                    <div key={i} className="relative group w-20 h-20 rounded-xl overflow-hidden border" data-testid={`order-ref-${idx}-${i}`}>
                                                                        <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
                                                                            <img src={src} alt={`Reference ${i + 1}`} className="w-full h-full object-cover" />
                                                                        </a>
                                                                        <button
                                                                            onClick={() => downloadPhoto(src, filename)}
                                                                            data-testid={`order-download-${idx}-${i}`}
                                                                            title="Download image"
                                                                            className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-white/95 hover:bg-primary hover:text-white text-primary flex items-center justify-center shadow"
                                                                        >
                                                                            <Download className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <h4 className="font-semibold uppercase text-xs tracking-widest text-muted-foreground mb-2">Internal notes</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                    {notes.map((n) => (
                                        <div key={n.id} className="rounded-xl bg-muted/40 p-3 text-sm">
                                            <div className="text-xs uppercase text-muted-foreground">{n.author_name} · {n.author_role} · {new Date(n.created_at).toLocaleString()}</div>
                                            <p className="mt-1 whitespace-pre-line">{n.body}</p>
                                        </div>
                                    ))}
                                    {notes.length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <Input placeholder="Add a note visible to team…" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="order-note-input" className="rounded-full h-10 bg-white/60" />
                                    <Button onClick={addNote} data-testid="order-note-add" className="rounded-full bg-primary hover:bg-primary/90 h-10"><MessageSquare className="w-4 h-4" /> Add</Button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Manual order dialog */}
            <Dialog open={!!manual} onOpenChange={(v) => !v && setManual(null)}>
                <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-order-dialog">
                    <DialogHeader><DialogTitle data-testid="manual-order-title">{manual?._editing ? `Edit order ${manual._orderNumber || ""}` : "Create manual order"}</DialogTitle></DialogHeader>
                    {manual && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder="Customer name" value={manual.address.full_name} onChange={(e) => setManual({ ...manual, address: { ...manual.address, full_name: e.target.value } })} data-testid="manual-name" className="rounded-xl h-10" />
                                <Input placeholder="Phone" value={manual.address.phone} onChange={(e) => setManual({ ...manual, address: { ...manual.address, phone: e.target.value } })} data-testid="manual-phone" className="rounded-xl h-10" />
                                <Input placeholder="Email" type="email" value={manual.address.email} onChange={(e) => setManual({ ...manual, address: { ...manual.address, email: e.target.value } })} data-testid="manual-email" className="rounded-xl h-10 col-span-2" />
                                <Input placeholder="Address line 1" value={manual.address.line1} onChange={(e) => setManual({ ...manual, address: { ...manual.address, line1: e.target.value } })} className="rounded-xl h-10 col-span-2" />
                                <Input placeholder="City" value={manual.address.city} onChange={(e) => setManual({ ...manual, address: { ...manual.address, city: e.target.value } })} className="rounded-xl h-10" />
                                <Input placeholder="State" value={manual.address.state} onChange={(e) => setManual({ ...manual, address: { ...manual.address, state: e.target.value } })} className="rounded-xl h-10" />
                                <Input placeholder="Pincode" value={manual.address.pincode} onChange={(e) => setManual({ ...manual, address: { ...manual.address, pincode: e.target.value } })} className="rounded-xl h-10" />
                                <Input placeholder="Country" value={manual.address.country} onChange={(e) => setManual({ ...manual, address: { ...manual.address, country: e.target.value } })} className="rounded-xl h-10" />
                            </div>
                            <div className="rounded-2xl bg-muted/40 p-3">
                                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</div>
                                {manual.items.map((item, idx) => {
                                    const prod = products.find((p) => p.id === item.product_id);
                                    return (
                                        <div key={idx} className="mb-3 pb-3 border-b border-border/60 last:border-b-0 last:mb-0 last:pb-0">
                                            <div className="grid grid-cols-6 gap-2 items-center mb-2">
                                                <select value={item.product_id} onChange={(e) => { const items = [...manual.items]; items[idx] = { ...item, product_id: e.target.value, size_label: products.find((p) => p.id === e.target.value)?.sizes?.[0]?.label || "" }; setManual({ ...manual, items }); }} className="col-span-3 rounded-xl border h-10 px-2 text-sm">
                                                    {!prod && item.product_id && <option value={item.product_id}>{item.product_name || "Current product (inactive)"}</option>}
                                                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                                <select value={item.size_label} onChange={(e) => { const items = [...manual.items]; items[idx] = { ...item, size_label: e.target.value }; setManual({ ...manual, items }); }} className="col-span-2 rounded-xl border h-10 px-2 text-sm">
                                                    {!prod && item.size_label && <option value={item.size_label}>{item.size_label}</option>}
                                                    {prod?.sizes?.map((s) => <option key={s.label} value={s.label}>{s.label} · {formatINR(s.price_inr)}</option>)}
                                                </select>
                                                <Input type="number" value={item.quantity} onChange={(e) => { const items = [...manual.items]; items[idx] = { ...item, quantity: Number(e.target.value) || 1 }; setManual({ ...manual, items }); }} className="rounded-xl h-10" />
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Reference photos {(item.reference_photos || []).length > 0 && `(${item.reference_photos.length})`}</div>
                                                    <label className="text-[11px] text-primary cursor-pointer inline-flex items-center gap-1" data-testid={`manual-photo-upload-${idx}`}>
                                                        <Plus className="w-3 h-3" /> Add photo
                                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadManualPhoto(idx, f); }} />
                                                    </label>
                                                </div>
                                                <div className="flex gap-2 flex-wrap">
                                                    {(item.reference_photos || []).map((p, pi) => {
                                                        const src = p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`;
                                                        return (
                                                            <div key={pi} className="relative w-16 h-16 rounded-lg overflow-hidden border" data-testid={`manual-photo-${idx}-${pi}`}>
                                                                <img src={src} alt="" className="w-full h-full object-cover" />
                                                                <button type="button" onClick={() => removeManualPhoto(idx, pi)} className="absolute top-0 right-0 bg-black/60 text-white w-5 h-5 flex items-center justify-center text-xs" data-testid={`manual-photo-remove-${idx}-${pi}`}>×</button>
                                                            </div>
                                                        );
                                                    })}
                                                    {(item.reference_photos || []).length === 0 && <div className="text-[11px] text-muted-foreground italic">No reference photos yet</div>}
                                                </div>
                                            </div>
                                            {manual._editing && (
                                                <div className="mt-3 pt-3 border-t border-dashed border-border">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Production files (STL/3MF/GLB) {(item.production_files || []).length > 0 && `(${item.production_files.length})`}</div>
                                                        <label className={`text-[11px] cursor-pointer inline-flex items-center gap-1 ${prodUpload?.idx === idx ? "text-muted-foreground pointer-events-none opacity-60" : "text-primary"}`} data-testid={`prod-file-upload-${idx}`}>
                                                            <Plus className="w-3 h-3" /> {prodUpload?.idx === idx ? "Uploading…" : "Upload STL/3MF/GLB"}
                                                            <input type="file" accept=".stl,.3mf,.glb,.gltf,.obj" className="hidden" disabled={prodUpload?.idx === idx} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadProductionFile(idx, f); }} />
                                                        </label>
                                                    </div>
                                                    {prodUpload?.idx === idx && (
                                                        <div className="mb-2" data-testid={`prod-file-progress-${idx}`}>
                                                            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                                                                <span className="truncate max-w-[60%]" title={prodUpload.name}>{prodUpload.name}</span>
                                                                <span data-testid={`prod-file-progress-pct-${idx}`}>{prodUpload.progress}%</span>
                                                            </div>
                                                            <div className="w-full h-1.5 rounded-full bg-primary/10 overflow-hidden">
                                                                <div
                                                                    className="h-full bg-primary transition-[width] duration-200"
                                                                    style={{ width: `${prodUpload.progress}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(item.staged_production_files || []).length > 0 && (
                                                        <div className="mb-3 space-y-2" data-testid={`prod-staged-${idx}`}>
                                                            <div className="text-[11px] px-2 py-1 rounded bg-sky-50 border border-sky-200 text-sky-900">
                                                                <b>Not yet visible to customer</b> — review the preview, then click <b>Save changes</b> to publish and notify them.
                                                            </div>
                                                            {(item.staged_production_files || []).map((p, si) => (
                                                                <div key={si} className="rounded-xl border border-sky-200 overflow-hidden" data-testid={`prod-staged-file-${idx}-${si}`}>
                                                                    <div className="flex items-center justify-between px-3 py-1.5 bg-sky-50/60 text-[11px]">
                                                                        <span className="truncate font-medium" title={p}>{p.split("/").pop()}</span>
                                                                        <button type="button" onClick={() => removeStagedFile(idx, p)} data-testid={`prod-staged-remove-${idx}-${si}`} className="text-destructive hover:underline inline-flex items-center gap-1"><X className="w-3 h-3" /> Discard</button>
                                                                    </div>
                                                                    {isMeshFile(p) ? (
                                                                        <div className="bg-gradient-to-br from-brand-cream to-brand-lavender">
                                                                            <STLViewer src={uploadHref(p)} height={240} testId={`prod-staged-viewer-${idx}-${si}`} />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="px-3 py-2 text-[11px] text-muted-foreground italic">Inline preview is available for STL, 3MF and GLB files.</div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {item.approval_status === "approved" && <div className="text-[11px] px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 mb-2">✓ Customer approved preview</div>}
                                                    {item.approval_status === "revision_requested" && (
                                                        <div className="text-[11px] px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900 mb-2" data-testid={`admin-revision-${idx}`}>
                                                            <b>Customer requested changes:</b> "{item.revision_note}"
                                                        </div>
                                                    )}
                                                    <div className="flex gap-2 flex-wrap">
                                                        {(item.production_files || []).map((p, fi) => (
                                                            <div key={fi} className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[11px] pr-1 overflow-hidden">
                                                                <a
                                                                    href={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    data-testid={`prod-file-${idx}-${fi}`}
                                                                    className="px-3 py-1 hover:bg-primary/20"
                                                                    title={p}
                                                                >
                                                                    {p.split("/").pop()}
                                                                </a>
                                                                {isMeshFile(p) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setPreviewFile(previewFile?.key === `${idx}-${fi}` ? null : { key: `${idx}-${fi}`, path: p })}
                                                                        data-testid={`prod-file-preview-${idx}-${fi}`}
                                                                        aria-label="Preview 3D"
                                                                        title={previewFile?.key === `${idx}-${fi}` ? "Hide preview" : "Preview 3D"}
                                                                        className="ml-1 w-5 h-5 rounded-full bg-white/80 hover:bg-primary hover:text-white text-primary inline-flex items-center justify-center"
                                                                    >
                                                                        {previewFile?.key === `${idx}-${fi}` ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeProductionFile(idx, p)}
                                                                    data-testid={`prod-file-remove-${idx}-${fi}`}
                                                                    aria-label="Remove file"
                                                                    title="Remove file"
                                                                    className="ml-1 w-5 h-5 rounded-full bg-white/80 hover:bg-destructive hover:text-white text-destructive inline-flex items-center justify-center"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {(item.production_files || []).length === 0 && <div className="text-[11px] text-muted-foreground italic">No production files yet — customer sees "Awaiting production" until uploaded</div>}
                                                    </div>
                                                    {previewFile && previewFile.key.startsWith(`${idx}-`) && (item.production_files || []).includes(previewFile.path) && (
                                                        <div className="mt-2 rounded-xl overflow-hidden border bg-gradient-to-br from-brand-cream to-brand-lavender" data-testid={`prod-file-preview-panel-${idx}`}>
                                                            <STLViewer src={uploadHref(previewFile.path)} height={240} testId={`prod-file-viewer-${idx}`} />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <button onClick={() => setManual({ ...manual, items: [...manual.items, { product_id: products[0]?.id, size_label: products[0]?.sizes?.[0]?.label, quantity: 1, reference_photos: [] }] })} className="text-xs text-primary">+ Add item</button>
                            </div>
                            <textarea placeholder="Notes" value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} className="w-full rounded-xl border p-2 text-sm min-h-[60px]" />
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={manual.mark_paid} onChange={(e) => setManual({ ...manual, mark_paid: e.target.checked })} /> Mark as paid (COD / bank transfer already received)</label>
                            <Button onClick={createManual} disabled={creating} data-testid="manual-order-save" className="w-full rounded-full h-11 bg-primary hover:bg-primary/90">
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : (manual?._editing ? (manual.items.some((it) => (it.staged_production_files || []).length) ? "Save changes & publish preview to customer" : "Save changes") : "Create order")}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminOrders;
